<template lang="html">
  <div class="LineupPlayer">
    <div :class="wrapperClass" :style="{ backgroundColor: color }">
      <profile-pic
        v-show="false"
        :size="35"
        :src="picture"
        :alt="name"
        circle
        border
        v-if="player.player_id"
        :number="playingInGames"></profile-pic>
      <div class="" :key="player.player_id">
        <div class="LineupPlayer__name text-lg text-semi">{{ name }}</div>
        <div class="LineupPlayer__position text-bold">
          <span>
            {{ player.position.short_name }}
          </span>
          <svg-icon
            v-if="isOutOfPosition"
            icon="alert-triangle"
            class="LineupPlayer__out-of-position"
            title="Player is out of preferred position" />

          <rating
            v-if="player.player_id && averageRating"
            :rating="averageRating" />
        </div>
      </div>
      <div :class="ratedClass" @click="toggleRate" v-if="player.player_id">
        <svg-icon icon="star"></svg-icon>
      </div>
      <div :class="dropdownContainerClass">
        <action
          icon="add"
          size="sm"
          v-if="!player.player_id && sortedPlayers"
          transparent
          @actionClicked="toggleAssignment"></action>
        <dropdown
          v-if="player.player_id"
          :items="items"
          keyProp="id"
          prop="label"
          dark
          @dropdown-item-click="handlePlayerAction" />
        <SwitchPlayer
          dark
          :teams="teams"
          v-if="showSwitch"
          :closeDropdown="toggleSwitch"
          @newPositionSelected="switchPosition"></SwitchPlayer>
      </div>
    </div>
    <portal to="modal">
      <RateModal
        v-if="showRateForm"
        :player="player"
        :toggle="toggleRate"
        :rating="playerRating"></RateModal>
      <NoteModal
        v-if="showNoteForm"
        :player="player"
        :toggle="toggleNote"></NoteModal>
      <AssignPlayerModal
        v-if="assignPlayer"
        :players="sortedPlayers"
        :team="team"
        :position="player.position"
        :toggle="toggleAssignment"
        :searchOptions="searchSettings"
        :color="color"
        :eventId="eventId"
        :game="game"
        @selectPlayer="addPlayerToPosition"></AssignPlayerModal>
    </portal>
  </div>
</template>

<script>
  import { mapState } from 'vuex';
  import SwitchPlayer from '@/components/Dropdowns/SwitchPlayer';
  import RateModal from '@/components/Modals/RateModal';
  import NoteModal from '@/components/Modals/NoteModal';
  import AssignPlayerModal from '@/components/Modals/AssignPlayerModal';

  export default {
    name: 'LineupPlayer',
    components: { RateModal, NoteModal, AssignPlayerModal, SwitchPlayer },
    props: {
      color: {
        type: String,
        required: true,
      },
      eventId: {
        type: Number,
        required: true,
      },
      game: {
        type: Object,
        required: true,
      },
      player: {
        type: Object,
        required: true,
      },
      team: {
        required: true,
      },
      availablePlayers: {
        type: [Array, Boolean],
        required: false,
      },
      teams: {
        type: [Object, Boolean],
        required: false,
      },
    },
    data: function () {
      return {
        showRateForm: false,
        showNoteForm: false,
        assignPlayer: false,
        showSwitch: false,
        searchSettings: {
          threshold: 0.4,
          keys: ['player.name', 'player.email'],
        },
        sortedPlayers: [],
        items: [
          {
            id: 'show',
            label: 'Show profile',
            icon: 'person',
          },
          {
            id: 'note',
            label: 'Create a note',
            icon: 'add-note',
          },
          {
            id: 'change',
            label: 'Swap positions',
            icon: 'switch',
          },
          {
            id: 'remove',
            label: 'Remove player',
            icon: 'remove-circle',
          },
        ],
      };
    },
    mounted() {
      this.sortPlayers(this.availablePlayers);
    },
    methods: {
      toggleRate() {
        this.showRateForm = !this.showRateForm;
      },
      toggleNote() {
        this.showNoteForm = !this.showNoteForm;
      },
      toggleSwitch() {
        this.showSwitch = false;
      },
      toggleAssignment() {
        this.assignPlayer = !this.assignPlayer;
      },
      handlePlayerAction(payload) {
        switch (payload.id) {
          case 'remove':
            this.removePlayerFromPosition();
            break;
          case 'change':
            this.showSwitch = true;
            break;
          case 'note':
            this.toggleNote();
            break;
          case 'show':
            this.$router.push(`/players/${this.player.player_id}`);
            break;
          default:
            this.changePositionForPlayer(payload);
            break;
        }
      },
      switchPosition(payload) {
        let data = {
          position1: payload.id,
          player2: this.player.player_id,
          position2: this.player.id,
          player1: payload.player_id,
          game: this.player.game_id,
        };
        this.$store.dispatch('games/SWITCH_PLAYERS', data);
      },
      sortPlayers(players) {
        let shortName = this.player.position.short_name;
        if (players.length > 0) {
          let sortable = [...players];
          this.sortedPlayers = sortable.sort((a, b) => {
            let x =
              a.player.preffered_positions.length > 1
                ? [
                    a.player.preffered_positions[0].short_name,
                    a.player.preffered_positions[1].short_name,
                  ].indexOf(shortName) > -1
                : false;
            let y =
              b.player.preffered_positions.length > 1
                ? [
                    b.player.preffered_positions[0].short_name,
                    b.player.preffered_positions[1].short_name,
                  ].indexOf(shortName) > -1
                : false;

            return y - x;
          });
        }
      },
      changePositionForPlayer(payload) {
        let player_id = this.player.player_id;
        this.$store.dispatch('games/UPDATE_PLAYER_LINEUP_POSITION', {
          id: payload.id,
          player_id: player_id,
          game_id: this.player.game_id,
        });
      },
      addPlayerToPosition(payload) {
        let data = {
          id: this.player.id,
          player_id: payload.player_id,
          game_id: this.player.game_id,
        };
        this.$store.dispatch('games/UPDATE_PLAYER_LINEUP_POSITION', data);
      },
      hexToRelativeLuminance(hex) {
        const h = hex.replace('#', '');
        const r = parseInt(h.substring(0, 2), 16) / 255;
        const g = parseInt(h.substring(2, 4), 16) / 255;
        const b = parseInt(h.substring(4, 6), 16) / 255;
        const toLinear = (c) =>
          c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        return (
          0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
        );
      },
      removePlayerFromPosition() {
        let data = {
          id: this.player.id,
          player_id: null,
          game_id: this.player.game_id,
        };
        this.$emit('playerRemoved');
        this.$store.dispatch('games/UPDATE_PLAYER_LINEUP_POSITION', data);
      },
    },
    computed: {
      ...mapState({
        ratedLineups: (state) => state.user.profile.rated,
        ratings: (state) => state.user.profile.ratings,
      }),
      averageRating() {
        const avg = this.player.player
          ? this.player.player.average_rating
          : null;
        return avg && avg[0] ? avg[0].rating : null;
      },
      playerRating() {
        return this.ratings.find(
          (item) => item.player_lineup_id == this.player.id,
        );
      },
      name() {
        let current = this.player.player;
        return current && current.name ? current.name : 'Unassigned';
      },
      ratedClass() {
        return this.ratedLineups.indexOf(this.player.id) !== -1
          ? 'LineupPlayer__rate LineupPlayer__rate--rated'
          : 'LineupPlayer__rate';
      },
      picture() {
        let current = this.player.player;
        return current && current.photo_url ? current.photo_url : null;
      },
      isUnassigned() {
        let current = this.player.player;
        return current && current.name ? false : true;
      },
      playingInGames() {
        let current = this.player.player;
        return current
          ? current.games.filter((x) => x.event_id == this.eventId).length
          : false;
      },
      isOutOfPosition() {
        const current = this.player.player;
        if (!current || !this.player.player_id) return false;
        const positions = current.preffered_positions || [];
        if (positions.length === 0) return false;
        return !positions.some(
          (pos) => pos.short_name === this.player.position.short_name,
        );
      },
      isLightBackground() {
        if (!this.color) return false;
        return this.hexToRelativeLuminance(this.color) > 0.4;
      },
      wrapperClass() {
        return {
          LineupPlayer__wrapper: true,
          'LineupPlayer__wrapper--light-bg': this.isLightBackground,
        };
      },
      dropdownContainerClass() {
        return this.player.player_id
          ? 'LineupPlayer__dropdown'
          : 'LineupPlayer__dropdown LineupPlayer__dropdown--unassigned';
      },
    },
    watch: {
      availablePlayers: {
        deep: true,
        handler(players) {
          this.sortPlayers(players);
        },
      },
    },
  };
</script>
